import { Asset as ContentfulAsset } from 'contentful';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import { IFullwidthHero20Fields } from '@/types/contentful';

import { usePageDataContext } from '~/context/PageData';

// import PrivacyPolicyDisclaimer from '~/components/PrivacyPolicyDisclaimer';
import { Asset as AssetComponent } from '~/components/AtomicComponents/Asset';
import { Eyebrow } from '~/components/Eyebrow';
import { getSplitHeroThemeClasses } from './theme';
import CtaWidget from '~/components/CtaWidget';
import PrivacyPolicyDisclaimer from '~/components/PrivacyPolicyDisclaimer';
import cx from 'classnames';
import PictogramComponent from '~/components/AtomicComponents/Pictogram';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import RichText from '~/components/RichText';

interface Props extends IFullwidthHero20Fields {
  isReturning?: boolean;
  theme?: string;
  affiliateHero?: boolean;
  leftPaneHeader?: ReactNode;
  affiliateDisclaimer?: Document | undefined;
}

export default function SplitHero({
  headingText,
  returningUserHeadingText,
  subheadingText,
  fullwidthDesktopImage,
  privacyPolicyDisclaimer,
  displayPrivacyPolicyIcon,
  desktopCta,
  mobileCta,
  eyebrow,
  mobileHeadingText,
  mobileSubheadingText,
  mobileImage,
  mobilePrivacyPolicyDisclaimer,
  theme,
  affiliateHero,
  leftPaneHeader,
  affiliateDisclaimer
}: Props) {
  const { advocateUserData } = usePageDataContext();

  const desktopVideoRef = useRef<HTMLVideoElement | null>(null);
  const mobileVideoRef = useRef<HTMLVideoElement | null>(null);
  const desktopVideoHandlersRef = useRef<{
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  }>();
  const mobileVideoHandlersRef = useRef<{
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
  }>();
  const [isDesktopVideoPlaying, setIsDesktopVideoPlaying] = useState(false);
  const [isMobileVideoPlaying, setIsMobileVideoPlaying] = useState(false);

  const themeClasses = getSplitHeroThemeClasses(theme);
  const desktopImageVisibility = fullwidthDesktopImage ? 'visible' : 'hidden';
  const isDesktopVideo =
    !!fullwidthDesktopImage?.fields?.file?.contentType?.includes('video');
  const mobileAsset =
    (mobileImage as ContentfulAsset) ??
    (fullwidthDesktopImage as ContentfulAsset);
  const isMobileVideo =
    !!mobileAsset?.fields?.file?.contentType?.includes('video');
  const themeKey = theme ?? 'brand';
  const pictogramType =
    themeKey === 'grape'
      ? 'accent1'
      : themeKey === 'lagoon'
      ? 'accent2'
      : themeKey === 'lime'
      ? 'accent3'
      : themeKey === 'tangerine'
      ? 'accent4'
      : themeKey === 'piggybank'
      ? 'accent5'
      : 'brand';
  const pictogramColor =
    themeKey === 'grape'
      ? 'grape'
      : themeKey === 'lagoon'
      ? 'lagoon'
      : themeKey === 'lime'
      ? 'lime'
      : themeKey === 'tangerine'
      ? 'tangerine'
      : themeKey === 'piggybank'
      ? 'piggybank'
      : undefined;

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <p className="typography-body-2 m-auto w-full whitespace-pre-wrap break-words text-left tablet:m-0 [&:not(:last-child)]:mb-4">
          {children}
        </p>
      );
    },
    [BLOCKS.HEADING_6]: (_node: Block | Inline, children) => (
      <p className={`typography-title-4 mb-1 font-medium`}>{children}</p>
    ),
    [INLINES.HYPERLINK]: (node, children) => {
      const href = node.data.uri as string;
      return (
        <a href={href} className="typography-title-4 mb-4 underline">
          {children}
        </a>
      );
    }
  };

  return (
    <section data-testid="fullwidth-hero-split">
      <div className="layout layout-padding">
        <div className="relative inline-flex w-full flex-col items-stretch justify-center gap-6 overflow-hidden desktop:flex-row min-[1250px]:items-stretch">
          <div
            className={cx(
              `w-full desktop:w-1/2 ${themeClasses.background} inline-flex flex-col items-start justify-center rounded-card-shape`,
              affiliateHero ? 'py-8' : 'py-10',
              'px-4 tablet:px-10 desktop:px-10 min-[1250px]:px-16',
              'desktop:h-[684px] min-[1250px]:aspect-square min-[1250px]:h-auto min-[1250px]:min-h-[586px]',
              'order-2 desktop:order-1'
            )}
          >
            {leftPaneHeader ? (
              <div className="mb-6 w-full tablet:mb-8 desktop:mb-auto">
                {leftPaneHeader}
              </div>
            ) : (
              eyebrow && (
                <Eyebrow
                  eyebrowFields={eyebrow.fields}
                  advocateData={advocateUserData}
                />
              )
            )}
            <div className="my-auto flex flex-col items-start justify-start self-stretch">
              {/* Headings */}
              <h1
                className={`hidden self-stretch text-left desktop:block ${themeClasses.headingText} typography-display-2 font-normal`}
              >
                {returningUserHeadingText ?? headingText ?? ''}
              </h1>
              <h2
                className={`block self-stretch text-left desktop:hidden ${themeClasses.headingText} typography-display-2 font-normal`}
              >
                {mobileHeadingText ?? headingText ?? ''}
              </h2>

              <div className="flex flex-col items-start justify-start self-stretch">
                {/* Spacers differ by breakpoint */}
                <div
                  data-size="md"
                  className="h-3 max-h-3 min-h-3 self-stretch desktop:h-4 desktop:max-h-4 desktop:min-h-4"
                />
                {/* Subheadings */}
                <p
                  className={`hidden self-stretch text-left desktop:block ${themeClasses.subheadingText} typography-display-5 font-normal`}
                >
                  {subheadingText ?? ''}
                </p>
                <p
                  className={`block self-stretch text-left desktop:hidden ${themeClasses.subheadingText} typography-display-5 font-normal`}
                >
                  {mobileSubheadingText ?? subheadingText ?? ''}
                </p>
              </div>

              {/* CTA spacers */}
              <div data-size="md" className="h-6 self-stretch tablet:h-8" />

              {/* Desktop CTA */}
              <div className="mb-1 hidden w-full flex-col items-start justify-center self-stretch rounded-card-shape tablet:max-w-[560px] desktop:flex desktop:max-w-[360px]">
                {desktopCta && (
                  <CtaWidget
                    {...desktopCta?.fields}
                    sysId={desktopCta?.sys?.contentType?.sys?.id}
                    id="fullwidth-hero-cta-alt"
                    module="Hero"
                    isMobile={false}
                    responsive
                    width={'100%'}
                  />
                )}
              </div>

              {/* Mobile CTA */}
              <div className="mb-1 flex w-full max-w-[360px] flex-col items-start justify-center self-stretch rounded-card-shape desktop:hidden">
                {mobileCta && (
                  <CtaWidget
                    {...mobileCta?.fields}
                    id="fullwidth-hero-mobile-cta-alt"
                    sysId={mobileCta?.sys?.contentType?.sys?.id}
                    module="Hero"
                    isMobile
                    width={'100%'}
                    responsive
                  />
                )}
              </div>

              {/* Privacy policy disclaimers */}
              {privacyPolicyDisclaimer && (
                <div
                  className={`mb-auto hidden items-center justify-start gap-2.5 self-stretch desktop:inline-flex ${themeClasses.subheadingText}`}
                >
                  <PrivacyPolicyDisclaimer
                    privacyPolicyDisclaimer={privacyPolicyDisclaimer}
                    displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                    colorHex={'currentColor'}
                    useAltLayout={true}
                  />
                </div>
              )}
              {mobilePrivacyPolicyDisclaimer && (
                <div
                  className={`mb-autoitems-center inline-flex justify-start gap-2.5 self-stretch desktop:hidden ${themeClasses.subheadingText} [&_p]:!text-left`}
                >
                  <PrivacyPolicyDisclaimer
                    privacyPolicyDisclaimer={
                      mobilePrivacyPolicyDisclaimer ?? privacyPolicyDisclaimer
                    }
                    displayPrivacyPolicyIcon={displayPrivacyPolicyIcon}
                    colorHex={'currentColor'}
                    noMargin={true}
                    useAltLayout={true}
                  />
                </div>
              )}
            </div>
            {/* Affiliate disclaimer */}
            {affiliateDisclaimer && affiliateHero && (
              <>
                <div className="mb-3 mt-6 h-px w-full bg-line tablet:mt-8 desktop:mt-auto" />
                <RichText
                  data={affiliateDisclaimer as any}
                  overrideOptions={RICH_TEXT_OVERRIDE}
                />
              </>
            )}
          </div>

          {/* Media column with responsive assets */}
          <div
            className="relative order-1 w-full overflow-hidden rounded-2xl object-cover desktop:order-2 desktop:h-[684px] desktop:min-h-[684px] desktop:w-1/2 min-[1250px]:aspect-square min-[1250px]:h-auto min-[1250px]:min-h-[586px] [&_img]:h-full [&_img]:max-w-none [&_img]:bg-center"
            style={{ visibility: desktopImageVisibility }}
          >
            {/* Desktop asset */}
            <div className="hidden h-full desktop:block">
              <AssetComponent
                asset={fullwidthDesktopImage as ContentfulAsset}
                loading="eager"
                className="flex h-full w-full items-center justify-center object-cover [&>img]:h-full [&>img]:w-full [&>img]:object-cover"
                onVideoRef={(el: HTMLVideoElement | null) => {
                  const prev = desktopVideoRef.current;
                  if (!desktopVideoHandlersRef.current) {
                    desktopVideoHandlersRef.current = {
                      onPlay: () => setIsDesktopVideoPlaying(true),
                      onPause: () => setIsDesktopVideoPlaying(false),
                      onEnded: () => setIsDesktopVideoPlaying(false)
                    };
                  }
                  if (prev && prev !== el) {
                    const h = desktopVideoHandlersRef.current;
                    prev.removeEventListener('play', h!.onPlay);
                    prev.removeEventListener('pause', h!.onPause);
                    prev.removeEventListener('ended', h!.onEnded);
                  }
                  desktopVideoRef.current = el;
                  if (el) {
                    const h = desktopVideoHandlersRef.current!;
                    el.addEventListener('play', h.onPlay);
                    el.addEventListener('pause', h.onPause);
                    el.addEventListener('ended', h.onEnded);
                    setIsDesktopVideoPlaying(!el.paused && !el.ended);
                  }
                }}
              />
              {isDesktopVideo && (
                <button
                  type="button"
                  aria-label={
                    isDesktopVideoPlaying ? 'Pause video' : 'Play video'
                  }
                  onClick={() => {
                    const v = desktopVideoRef.current;
                    if (!v) return;
                    if (v.paused) {
                      if (v.ended) v.currentTime = 0;
                      v.play().catch(() => undefined);
                      setIsDesktopVideoPlaying(true);
                    } else {
                      v.pause();
                      setIsDesktopVideoPlaying(false);
                    }
                  }}
                  className="absolute bottom-10 right-10 flex h-10 w-10 items-center justify-center rounded-full bg-brand-base"
                >
                  <span className="sr-only">
                    {isDesktopVideoPlaying ? 'Pause' : 'Play'}
                  </span>
                  <div className="pointer-events-none">
                    {isDesktopVideoPlaying ? (
                      <div className="flex items-center justify-center gap-1">
                        <div className="h-3.5 w-1 bg-brand-strongest" />
                        <div className="h-3.5 w-1 bg-brand-strongest" />
                      </div>
                    ) : (
                      <PictogramComponent
                        size="sm"
                        type={pictogramType as any}
                        color={pictogramColor as any}
                        icon="ic_play_filled"
                        shape="circle"
                      />
                    )}
                  </div>
                </button>
              )}
            </div>

            {/* Mobile asset */}
            <div className="relative block aspect-square w-full overflow-hidden rounded-card-shape min-[600px]:min-h-[508px] desktop:hidden">
              <AssetComponent
                asset={
                  (mobileImage as ContentfulAsset) ??
                  (fullwidthDesktopImage as ContentfulAsset)
                }
                loading="eager"
                className="flex h-full w-full items-center justify-center [&_img]:h-full [&_img]:w-full [&_img]:overflow-hidden [&_img]:rounded-card-shape [&_img]:object-cover"
                onVideoRef={(el: HTMLVideoElement | null) => {
                  const prev = mobileVideoRef.current;
                  if (!mobileVideoHandlersRef.current) {
                    mobileVideoHandlersRef.current = {
                      onPlay: () => setIsMobileVideoPlaying(true),
                      onPause: () => setIsMobileVideoPlaying(false),
                      onEnded: () => setIsMobileVideoPlaying(false)
                    };
                  }
                  if (prev && prev !== el) {
                    const h = mobileVideoHandlersRef.current;
                    prev.removeEventListener('play', h!.onPlay);
                    prev.removeEventListener('pause', h!.onPause);
                    prev.removeEventListener('ended', h!.onEnded);
                  }
                  mobileVideoRef.current = el;
                  if (el) {
                    const h = mobileVideoHandlersRef.current!;
                    el.addEventListener('play', h.onPlay);
                    el.addEventListener('pause', h.onPause);
                    el.addEventListener('ended', h.onEnded);
                    setIsMobileVideoPlaying(!el.paused && !el.ended);
                  }
                }}
              />
              {isMobileVideo && (
                <button
                  type="button"
                  aria-label={
                    isMobileVideoPlaying ? 'Pause video' : 'Play video'
                  }
                  onClick={() => {
                    const v = mobileVideoRef.current;
                    if (!v) return;
                    if (v.paused) {
                      if (v.ended) v.currentTime = 0;
                      v.play().catch(() => undefined);
                      setIsMobileVideoPlaying(true);
                    } else {
                      v.pause();
                      setIsMobileVideoPlaying(false);
                    }
                  }}
                  className="absolute bottom-8 right-8 flex h-10 w-10 items-center justify-center rounded-full bg-brand-base"
                >
                  <span className="sr-only">
                    {isMobileVideoPlaying ? 'Pause' : 'Play'}
                  </span>
                  <div className="pointer-events-none">
                    {isMobileVideoPlaying ? (
                      <div className="flex items-center justify-center gap-1">
                        <div className="h-3.5 w-1 bg-brand-strongest" />
                        <div className="h-3.5 w-1 bg-brand-strongest" />
                      </div>
                    ) : (
                      <PictogramComponent
                        size="sm"
                        type={pictogramType as any}
                        color={pictogramColor as any}
                        icon="ic_play_filled"
                        shape="circle"
                      />
                    )}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
